/**
 * AI Import Hooks
 * React Query hooks for the Claude-powered file ingestion pipeline.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as aiImportService from '@/services/ai-import.service';
import type {
  AIImportJob,
  AIImportJobFileFiling,
  AIImportJobFilters,
  AIImportJobUpdate,
  AIImportFeedbackInsert,
  AIImportQuestionStatus,
  AIImportPlan,
} from '@/types/ai-import';

// ============================================================================
// Query keys
// ============================================================================

export const aiImportKeys = {
  all: ['ai-imports'] as const,
  lists: () => [...aiImportKeys.all, 'list'] as const,
  list: (filters?: AIImportJobFilters) => [...aiImportKeys.lists(), { filters }] as const,
  details: () => [...aiImportKeys.all, 'detail'] as const,
  detail: (id: string) => [...aiImportKeys.details(), id] as const,
  files: (jobId: string) => [...aiImportKeys.detail(jobId), 'files'] as const,
  questions: (jobId: string) => [...aiImportKeys.detail(jobId), 'questions'] as const,
  emails: (jobId: string) => [...aiImportKeys.detail(jobId), 'emails'] as const,
  candidates: (jobId: string) => [...aiImportKeys.detail(jobId), 'candidates'] as const,
  pendingCandidates: () => [...aiImportKeys.all, 'candidates', 'pending'] as const,
  analytics: () => [...aiImportKeys.all, 'analytics'] as const,
  // Email intake v2. Filings are keyed by the file ids they were read for (one detail page reads
  // its job's files); `filings()` is the prefix a retry invalidates.
  filings: () => [...aiImportKeys.all, 'filings'] as const,
  filingsFor: (fileIds: readonly string[]) => [...aiImportKeys.filings(), fileIds.join(',')] as const,
  openLaneLeads: () => [...aiImportKeys.all, 'open-lane-leads'] as const,
  // The job's own lead (what was already sent its sender). Under detail(jobId), so a decline that succeeds
  // and invalidates the job re-reads it too.
  jobLeads: (jobId: string) => [...aiImportKeys.detail(jobId), 'leads'] as const,
};

const ACTIVE_STATUSES: AIImportJob['status'][] = ['pending', 'running'];

/** True while the worker is still touching this job's rows. */
export function isJobActive(status: AIImportJob['status'] | undefined | null): boolean {
  return !!status && ACTIVE_STATUSES.includes(status);
}

/**
 * Async analysis generation is fire-and-forget and can silently never land
 * (generation failed, or the job predates migration 109). This is a generous
 * budget after which a surface can show a terminal "no analysis" state instead
 * of a spinner that never resolves.
 */
export const ANALYSIS_GENERATION_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * True once we should stop expecting an analysis for a job: no analysis content
 * has landed and the budget has elapsed since analysis could have started.
 * Anchored to completed_at (extraction finished ≈ analysis began), NOT
 * created_at — a slow multi-file extraction can itself run long, and anchoring
 * to created_at would wrongly mark a just-starting analysis as timed out.
 */
export function isAnalysisTimedOut(job: AIImportJob): boolean {
  if (hasAnalysisLanded(job)) return false;
  const since = new Date(job.completed_at || job.created_at).getTime();
  if (Number.isNaN(since)) return false;
  return Date.now() - since > ANALYSIS_GENERATION_TIMEOUT_MS;
}

/**
 * Has any report output landed on the job? The structured analysis, the narrative, or the one-line takeaway
 * (the report function writes all three in one update, so a job with only the takeaway is one that will never
 * have the others: the open lane's "No property found" notice and an enrichment digest write nothing else).
 * These are the three columns AnalysisPanel counts as "something to show", so the spinner, the timeout and the
 * polling all stop for the same jobs.
 */
export function hasAnalysisLanded(
  job: Pick<AIImportJob, 'report_analysis' | 'report_narrative_html' | 'report_one_line_takeaway'>,
): boolean {
  return !!(job.report_analysis || job.report_narrative_html || job.report_one_line_takeaway);
}

/** Refreshes of a job after which we stop waiting for an analysis (~3 minutes at 3 s each). */
const ANALYSIS_POLL_MAX_UPDATES = 60;

/**
 * How long useAIImportJob waits before it asks for the job again, or false to stop. A pending or running job is
 * asked for every 2 s; a job that has finished but whose analysis has not landed yet (generation is
 * fire-and-forget) every 3 s, for a bounded number of refreshes; anything else not at all.
 */
export function jobPollInterval(job: AIImportJob | null | undefined, updates: number): number | false {
  if (!job) return false;
  if (ACTIVE_STATUSES.includes(job.status)) return 2000;
  const analysisPending =
    (job.status === 'completed_pending_review' || job.status === 'applied') && !hasAnalysisLanded(job);
  // ~3 minutes of patience, then stop — generation failed or predates 109.
  return analysisPending && updates < ANALYSIS_POLL_MAX_UPDATES ? 3000 : false;
}

// ============================================================================
// Queries
// ============================================================================

export function useAIImportJobs(filters?: AIImportJobFilters) {
  return useQuery({
    queryKey: aiImportKeys.list(filters),
    queryFn: () => aiImportService.getJobs(filters),
    staleTime: 30_000,
    // Poll while any returned job is still pending/running so the list's inline
    // progress reflects reality instead of freezing at its load-time value; stop
    // once nothing is active. Mirrors useAIImportJob's polling pattern.
    refetchInterval: (query) => {
      const data = query.state.data as AIImportJob[] | undefined;
      if (!data) return false;
      return data.some((j) => ACTIVE_STATUSES.includes(j.status)) ? 4000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

/**
 * Single-job query. Polls every 2s while the job is pending or running, and
 * keeps polling (3s, bounded) after extraction while the async analysis
 * (generated fire-and-forget by the worker; see hasAnalysisLanded for what
 * counts as landed) has not landed yet — the AnalysisPanel appears without a
 * manual refresh. See jobPollInterval.
 */
export function useAIImportJob(id: string | undefined) {
  return useQuery({
    queryKey: id ? aiImportKeys.detail(id) : ['ai-imports', 'detail', 'noop'],
    queryFn: () => aiImportService.getJob(id!),
    enabled: !!id,
    refetchInterval: (query) =>
      jobPollInterval(query.state.data as AIImportJob | null | undefined, query.state.dataUpdateCount),
    refetchIntervalInBackground: false,
  });
}

/**
 * A job's file rows. Each row walks queued → uploading_to_anthropic →
 * classifying → extracting → done over the life of a run, so pass
 * `live: isJobActive(job?.status)` from any surface that renders those states
 * while the worker runs. Without it the list is fetched once when the surface
 * mounts and every row reads "Queued" for the whole import, next to a job
 * progress bar that is visibly moving.
 *
 * `live` is driven by the JOB's status, not by the file states: a cancelled or
 * failed job leaves rows sitting at queued forever, and polling on those would
 * never stop.
 */
export function useAIImportJobFiles(jobId: string | undefined, opts?: { live?: boolean }) {
  return useQuery({
    queryKey: jobId ? aiImportKeys.files(jobId) : ['ai-imports', 'files', 'noop'],
    queryFn: () => aiImportService.getJobFiles(jobId!),
    enabled: !!jobId,
    refetchInterval: opts?.live ? 2000 : false,
    refetchIntervalInBackground: false,
    // Always refetch on mount rather than inheriting the 5-minute global
    // staleTime: the review step mounts the instant extraction ends and reads
    // processing_state to surface unreadable files, so it must not reuse the
    // snapshot the last poll happened to catch mid-run.
    staleTime: 0,
  });
}

/**
 * Cumulative analytics across all AI import jobs — what AI has derived
 * (properties, files, comps, spend). Used by the AI Imports dashboard header.
 */
export function useAIImportAnalytics() {
  return useQuery({
    queryKey: aiImportKeys.analytics(),
    queryFn: () => aiImportService.getAIImportAnalytics(),
    staleTime: 60_000,
  });
}

export function useAIImportQuestions(jobId: string | undefined) {
  return useQuery({
    queryKey: jobId ? aiImportKeys.questions(jobId) : ['ai-imports', 'questions', 'noop'],
    queryFn: () => aiImportService.getJobQuestions(jobId!),
    enabled: !!jobId,
  });
}

export function useAIImportJobEmails(jobId: string | undefined) {
  return useQuery({
    queryKey: jobId ? aiImportKeys.emails(jobId) : ['ai-imports', 'emails', 'noop'],
    queryFn: () => aiImportService.getJobEmails(jobId!),
    enabled: !!jobId,
  });
}

/**
 * Newsletter-enrichment listing candidates awaiting triage. Only queried for
 * enrichment jobs (pass `enabled = job.summary?.job_kind === 'enrichment'`).
 */
export function useListingCandidates(jobId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: jobId ? aiImportKeys.candidates(jobId) : ['ai-imports', 'candidates', 'noop'],
    queryFn: () => aiImportService.fetchListingCandidates(jobId!),
    enabled: !!jobId && enabled,
  });
}

/** Every listing still awaiting a human, across all import jobs. */
export function usePendingListingCandidates() {
  return useQuery({
    queryKey: aiImportKeys.pendingCandidates(),
    queryFn: () => aiImportService.fetchPendingListingCandidates(),
  });
}

/**
 * Where a job's files were filed under a property in SharePoint. Filing runs off the apply path (a nudge, then
 * the watchdog's sweep), so rows sit `pending` or `filing` for a while: poll while any does, so the page's
 * "Filing…" resolves without a reload. Bounded: a row that never settles, or a run of failed polls, stops it.
 */
export function useAIImportFilings(fileIds: string[]) {
  return useQuery({
    queryKey: aiImportKeys.filingsFor(fileIds),
    queryFn: () => aiImportService.getFilingsForFiles(fileIds),
    enabled: fileIds.length > 0,
    staleTime: 15_000,
    refetchInterval: (query) => {
      const rows = query.state.data as AIImportJobFileFiling[] | undefined;
      const inFlight = !!rows?.some((r) => r.state === 'pending' || r.state === 'filing');
      if (!inFlight) return false;
      return query.state.dataUpdateCount < 60 && query.state.errorUpdateCount < 3 ? 5000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

/**
 * The lead of an open-lane job's inbound message: has its sender already been declined (`decline_sent_at`)? Only
 * asked while the page would otherwise offer Send decline (`enabled`), so an ordinary job costs no query.
 */
export function useJobLeads(jobId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: jobId ? aiImportKeys.jobLeads(jobId) : ['ai-imports', 'detail', 'noop', 'leads'],
    queryFn: () => aiImportService.getLeadsForJob(jobId!),
    enabled: !!jobId && enabled,
    staleTime: 30_000,
  });
}

/**
 * The open lane's audit rows. `enabled` is the Open lane activity card being expanded: the card is collapsed
 * by default and must not add a query to every load of the AI Imports page.
 */
export function useOpenLaneLeads(enabled: boolean) {
  return useQuery({
    queryKey: aiImportKeys.openLaneLeads(),
    queryFn: () => aiImportService.getOpenLaneLeads(50),
    enabled,
    staleTime: 30_000,
  });
}

// ============================================================================
// Mutations
// ============================================================================

export function useCreateAIImportJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: aiImportService.SubmitJobPayload) => aiImportService.submitJob(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.lists() });
    },
  });
}

export function useUpdateImportPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, summary }: { id: string; summary: AIImportPlan }) =>
      aiImportService.updateJobSummary(id, summary),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.detail(job.id) });
    },
  });
}

export function useUpdateAIImportJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, update }: { id: string; update: AIImportJobUpdate }) =>
      aiImportService.updateJob(id, update),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.detail(job.id) });
      queryClient.invalidateQueries({ queryKey: aiImportKeys.lists() });
    },
  });
}

export function useCancelAIImportJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => aiImportService.cancelJob(id),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.detail(job.id) });
      queryClient.invalidateQueries({ queryKey: aiImportKeys.lists() });
    },
  });
}

export function useRerunAIImportJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { jobId: string; userName: string; userEmail: string | null }) =>
      aiImportService.rerunAIImportJob(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.lists() });
    },
  });
}

export function useApplyAIImportJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      applied_by,
      applied_by_email,
    }: {
      id: string;
      applied_by: string;
      applied_by_email: string | null;
    }) => aiImportService.applyJob(id, applied_by, applied_by_email),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: aiImportKeys.lists() });
      // Likely created a new deal — refresh the deals list too
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

/**
 * Regenerate a job's analysis under a chosen underwriting scenario
 * (conservative/base/aggressive) in place — no email, no new job. On success the
 * detail query is invalidated and the panel repaints with the new report_analysis.
 */
export function useRegenerateAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      scenario,
      scenarioOnly = true,
    }: {
      id: string;
      scenario: aiImportService.UnderwritingScenario;
      scenarioOnly?: boolean;
    }) => aiImportService.regenerateAnalysis(id, scenario, scenarioOnly),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.detail(id) });
    },
  });
}

export function useRollbackAIImportJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      rolled_back_by,
      rolled_back_by_email,
    }: {
      id: string;
      rolled_back_by: string;
      rolled_back_by_email: string | null;
    }) => aiImportService.rollbackJob(id, rolled_back_by, rolled_back_by_email),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: aiImportKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });
}

export function useResolveQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      questionId: string;
      jobId: string;
      status: AIImportQuestionStatus;
      resolved_value?: unknown;
      resolved_by: string;
    }) =>
      aiImportService.resolveQuestion(vars.questionId, {
        status: vars.status,
        resolved_value: vars.resolved_value,
        resolved_by: vars.resolved_by,
      }),
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.questions(jobId) });
      queryClient.invalidateQueries({ queryKey: aiImportKeys.detail(jobId) });
    },
  });
}

/**
 * Fire-and-forget feedback recording. Does not block UI — the review screen
 * calls this whenever the user diverges from Claude's output.
 */
export function useRecordFeedback() {
  return useMutation({
    mutationFn: (insert: AIImportFeedbackInsert) => aiImportService.recordFeedback(insert),
  });
}

/**
 * Approve (promote → research property) or reject a listing candidate. Both
 * decisions route through ai-import-apply (service role bypasses the SELECT-only
 * RLS on listing_candidates).
 */
export function useReviewListingCandidate(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      candidateId: string;
      decision: 'approved' | 'rejected';
      userName: string;
      userEmail: string;
    }) => aiImportService.reviewListingCandidate({ jobId, ...args }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.candidates(jobId) });
      queryClient.invalidateQueries({ queryKey: aiImportKeys.detail(jobId) });
      queryClient.invalidateQueries({ queryKey: aiImportKeys.pendingCandidates() });
    },
  });
}

/**
 * Approve (promote → property + valuation) or reject many candidates at once.
 *
 * Candidates are grouped by their originating import job so each edge-function
 * call carries a job_id that actually owns the rows it names — the review queue
 * spans every job, and a single borrowed job_id would misattribute the batch.
 */
/**
 * Stand-in job id for candidates orphaned by a deleted import job. The edge
 * function requires a non-empty job_id but resolves the real one per candidate,
 * so this only has to be present, not correct.
 */
const ORPHAN_JOB_KEY = '00000000-0000-0000-0000-000000000000';

export function useReviewListingCandidates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      candidates: Array<{ id: string; ai_import_job_id: string | null }>;
      decision: 'approved' | 'rejected';
      userName: string;
      userEmail: string;
    }) => {
      const byJob = new Map<string, string[]>();
      for (const c of args.candidates) {
        // ai_import_job_id is ON DELETE SET NULL (migration 104), so a candidate
        // whose job row was deleted carries null — and the edge function rejects
        // a null job_id outright, which would make such a row permanently
        // un-approvable AND un-rejectable. Group those under a sentinel; the
        // function reads each candidate's own job id anyway and only falls back
        // to the payload's.
        const key = c.ai_import_job_id ?? ORPHAN_JOB_KEY;
        byJob.set(key, [...(byJob.get(key) ?? []), c.id]);
      }
      let promoted = 0;
      let rejected = 0;
      const failures: string[] = [];
      for (const [jobId, candidateIds] of byJob) {
        try {
          const res = (await aiImportService.reviewListingCandidates({
            jobId,
            candidateIds,
            decision: args.decision,
            userName: args.userName,
            userEmail: args.userEmail,
          })) as { promoted_property_ids?: string[]; promoted_deal_ids?: string[]; rejected?: number } | null;
          // Both kinds count. Omitting deals reported "0 of 45 promoted" as a
          // SUCCESS toast right after creating 45 of them.
          promoted += (res?.promoted_property_ids?.length ?? 0) + (res?.promoted_deal_ids?.length ?? 0);
          rejected += res?.rejected ?? 0;
        } catch (err) {
          // One bad job must not sink the rest of the batch; report what failed.
          failures.push(err instanceof Error ? err.message : String(err));
        }
      }
      return { promoted, rejected, failures };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.pendingCandidates() });
      queryClient.invalidateQueries({ queryKey: aiImportKeys.all });
    },
  });
}

/**
 * Undo enrichment changes for a job — all of them, or specific digest indexes.
 * Reverses audited field updates + inserted listing valuation rows.
 */
export function useUndoEnrichment(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { digestIndexes?: number[]; userEmail: string }) =>
      aiImportService.undoEnrichmentChanges({ jobId, ...args }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.detail(jobId) });
      queryClient.invalidateQueries({ queryKey: aiImportKeys.candidates(jobId) });
    },
  });
}

/**
 * Retry filing: re-queue this job's failed filings (and any file that never got a row), then drain. Settles
 * either way into a re-read of every filings query: a call that errors after part of the drain has still
 * moved rows.
 */
export function useRetryFiling(jobId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!jobId) throw new Error('There is no import to file for.');
      return aiImportService.retryFiling(jobId);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.filings() });
    },
  });
}

/**
 * Send the branded decline to an open-lane job's sender. On success the job is re-read: send-ai-import-report
 * records open_lane_outcome = 'declined_by_staff' on it, which is what takes the button away.
 */
export function useSendOpenLaneDecline(jobId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!jobId) throw new Error('There is no import to decline.');
      return aiImportService.sendOpenLaneDecline(jobId);
    },
    onSuccess: () => {
      if (jobId) queryClient.invalidateQueries({ queryKey: aiImportKeys.detail(jobId) });
    },
  });
}

/**
 * "Treat as property" on a declined, capped or dropped lead. Settles into a re-read of the list either way:
 * when the write was refused because the lead had already moved on, the re-read is what corrects the row.
 */
export function useRequestOpenLaneOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) => aiImportService.requestOpenLaneOverride(leadId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: aiImportKeys.openLaneLeads() });
    },
  });
}
